// SoundCloud embed + tab-audio capture for the Studio's (hidden) SoundCloud tab.
//
// Same shape as ytCapture.ts: SoundCloud's own Widget streams the track into an
// in-page iframe (the user's own IP/session — nothing for SoundCloud to block),
// we capture THIS tab's audio via getDisplayMedia while it plays, and the
// recorded blob drops into the exact same separation flow as an uploaded file.
//
// The trick that makes it "the same as YouTube" for free: the SoundCloud Widget
// API is wrapped as a `YtPlayer`, so `startCapture()` (from ytCapture.ts) drives
// it unchanged — content-clock recording, freeze-pause, the works. SoundCloud
// works in ms; the adapter converts to the seconds `YtPlayer` speaks.

import { YtPlayer, YT_STATE } from './ytCapture';

/* ── minimal SoundCloud Widget API surface ──────────────────────────── */

interface ScSound {
  title?: string;
  duration?: number; // ms
  artwork_url?: string | null;
  user?: { username?: string; avatar_url?: string | null };
}

interface ScWidget {
  bind(event: string, listener: (e?: unknown) => void): void;
  play(): void;
  pause(): void;
  seekTo(ms: number): void;
  setVolume(pct: number): void;
  getPosition(cb: (ms: number) => void): void;
  getDuration(cb: (ms: number) => void): void;
  getCurrentSound(cb: (sound: ScSound | null) => void): void;
}

interface ScWidgetFactory {
  (el: HTMLIFrameElement | string): ScWidget;
  Events: {
    READY: string;
    PLAY: string;
    PAUSE: string;
    FINISH: string;
    PLAY_PROGRESS: string;
    ERROR: string;
  };
}

interface ScNamespace {
  Widget: ScWidgetFactory;
}

declare global {
  interface Window {
    SC?: ScNamespace;
  }
}

let scApiPromise: Promise<ScNamespace> | null = null;

/** Inject https://w.soundcloud.com/player/api.js once and resolve with SC. */
export function loadSoundCloudAPI(): Promise<ScNamespace> {
  if (scApiPromise) return scApiPromise;
  scApiPromise = new Promise<ScNamespace>((resolve, reject) => {
    if (window.SC?.Widget) {
      resolve(window.SC);
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://w.soundcloud.com/player/api.js';
    s.onload = () => (window.SC?.Widget ? resolve(window.SC) : reject(new Error('sc-api-missing')));
    s.onerror = () => {
      scApiPromise = null;
      reject(new Error('sc-api-load-failed'));
    };
    document.head.appendChild(s);
  });
  return scApiPromise;
}

/**
 * Canonical track URL from any pasteable SoundCloud link, or null.
 * Accepts `soundcloud.com/<user>/<track>` (with optional query, e.g. share
 * `?si=…`). Rejects playlists (`/sets/`), profiles, and the site's own
 * reserved sections.
 */
export function parseSoundCloudUrl(raw: string): string | null {
  try {
    const u = new URL(/^https?:/i.test(raw) ? raw : 'https://' + raw);
    // Short share links (on.soundcloud.com/xxxx) can't be resolved without a
    // round-trip, so ask for the full URL instead.
    if (!/(^|\.)soundcloud\.com$/i.test(u.hostname)) return null;
    const segs = u.pathname.split('/').filter(Boolean);
    if (segs.length < 2) return null; // need user + track
    if (segs[1].toLowerCase() === 'sets') return null; // playlist, not a track
    const reserved = new Set([
      'you', 'discover', 'search', 'stream', 'upload', 'settings', 'pages',
      'popular', 'charts', 'people', 'tags', 'messages', 'notifications',
    ]);
    if (reserved.has(segs[0].toLowerCase())) return null;
    return `https://soundcloud.com/${segs[0]}/${segs[1]}`;
  } catch {
    return null;
  }
}

export interface ScPlayer extends YtPlayer {
  /** Track metadata, populated by the time the promise resolves. */
  title: string;
  author: string;
  artwork: string;
}

/**
 * Point `iframe` at the SoundCloud widget for `trackUrl` and resolve with a
 * `YtPlayer`-compatible controller once the track's metadata is loaded.
 * Rejects if the widget errors or never becomes ready (private / geo-blocked /
 * embedding disabled).
 */
export function createScPlayer(iframe: HTMLIFrameElement, trackUrl: string): Promise<ScPlayer> {
  return loadSoundCloudAPI().then(
    (SC) =>
      new Promise<ScPlayer>((resolve, reject) => {
        // visual=false → the compact text+waveform player.
        //
        // PLAYBACK UNLOCK MODEL: a programmatic widget.play() sent over
        // postMessage is blocked by the browser's autoplay gate until the
        // IFRAME's own document has user activation — a click on OUR page
        // doesn't cross the boundary, and an auto_play=true prime proved
        // untrustworthy (the widget can end up "playing" silently when audible
        // autoplay is denied). So the FIRST play must be the user clicking the
        // SoundCloud player's own ▶ button; that one in-iframe click unlocks
        // audio for good, after which every programmatic play()/seekTo —
        // including the ones startCapture fires while recording — just works.
        // The capture panel surfaces this with a "press play on the player"
        // hint until it observes real playback.
        //
        // show_artwork=false: the widget gets a CSS invert filter for dark mode
        // (SoundCloud ships no dark theme), which would mangle the artwork's
        // colors — so the panel renders the artwork itself, outside the filter.
        const params = new URLSearchParams({
          url: trackUrl,
          auto_play: 'false',
          show_artwork: 'false',
          hide_related: 'true',
          show_comments: 'false',
          show_user: 'false',
          show_reposts: 'false',
          show_teaser: 'false',
          visual: 'false',
          buying: 'false',
          sharing: 'false',
          download: 'false',
        });
        iframe.src = 'https://w.soundcloud.com/player/?' + params.toString();

        const widget = SC.Widget(iframe);
        let posMs = 0;
        let durMs = 0;
        let state: number = YT_STATE.UNSTARTED;
        let title = '';
        let author = '';
        let artwork = '';
        let settled = false;

        const player: ScPlayer = {
          get title() {
            return title;
          },
          get author() {
            return author;
          },
          get artwork() {
            return artwork;
          },
          getCurrentTime: () => posMs / 1000,
          getDuration: () => durMs / 1000,
          getPlayerState: () => state,
          seekTo: (seconds: number) => {
            try {
              widget.seekTo(Math.max(0, seconds * 1000));
            } catch {
              /* widget gone */
            }
          },
          playVideo: () => {
            try {
              widget.play();
            } catch {
              /* widget gone */
            }
          },
          pauseVideo: () => {
            try {
              widget.pause();
            } catch {
              /* widget gone */
            }
          },
          mute: () => {
            try {
              widget.setVolume(0);
            } catch {
              /* widget gone */
            }
          },
          unMute: () => {
            try {
              widget.setVolume(100);
            } catch {
              /* widget gone */
            }
          },
          destroy: () => {
            /* the iframe is torn down when React unmounts the panel */
          },
        };

        const failTimer = window.setTimeout(() => {
          if (!settled) {
            settled = true;
            reject(new Error('sc-widget-timeout'));
          }
        }, 12_000);

        widget.bind(SC.Widget.Events.READY, () => {
          widget.getCurrentSound((snd) => {
            if (snd) {
              title = snd.title || '';
              author = snd.user?.username || '';
              // The API returns a small artwork; bump it to a crisp size, and
              // fall back to the uploader's avatar for tracks with no art.
              const raw = snd.artwork_url || snd.user?.avatar_url || '';
              artwork = raw ? raw.replace('-large', '-t200x200') : '';
              if (typeof snd.duration === 'number' && snd.duration > 0) durMs = snd.duration;
            }
            // Backstop the duration from getDuration if the sound didn't carry it.
            widget.getDuration((d) => {
              if (d > 0) durMs = d;
              if (!settled) {
                settled = true;
                window.clearTimeout(failTimer);
                resolve(player);
              }
            });
          });
        });

        widget.bind(SC.Widget.Events.PLAY, () => {
          state = YT_STATE.PLAYING;
        });
        widget.bind(SC.Widget.Events.PAUSE, () => {
          state = YT_STATE.PAUSED;
        });
        widget.bind(SC.Widget.Events.FINISH, () => {
          state = YT_STATE.ENDED;
        });
        // The sync clock startCapture() polls: SoundCloud pushes currentPosition
        // (ms) throughout playback.
        widget.bind(SC.Widget.Events.PLAY_PROGRESS, (e) => {
          const cp = (e as { currentPosition?: number } | undefined)?.currentPosition;
          if (typeof cp === 'number') posMs = cp;
        });
        widget.bind(SC.Widget.Events.ERROR, () => {
          if (!settled) {
            settled = true;
            window.clearTimeout(failTimer);
            reject(new Error('sc-widget-error'));
          }
        });
      }),
  );
}

/** onError / load-failure copy for the SoundCloud embed. */
export function scEmbedErrorMessage(): string {
  return 'This track can’t be played here — it may be private, region-locked, or the artist disabled embedding. Try another link, or use the Upload tab with an audio file.';
}
